/**
 * Encodes item kind, file family, provenance, and lock state in one visual.
 *
 * Central icon semantics keep connected/read-only items distinguishable across
 * list, grid, breadcrumb, and inspector views without letting each surface
 * invent conflicting security cues.
 */
import { LockKeyhole } from 'lucide-react'
import { cn } from '@/components/ui'
import type { VaultKind } from '../types'

interface FileTint {
  tile: string
  outline: string
  fold: string
  line: string
}

const DEFAULT_TINT: FileTint = {
  tile: 'bg-slate-100',
  outline: 'stroke-slate-300',
  fold: 'fill-slate-300',
  line: 'stroke-slate-400',
}

// Tint files by extension family so users can scan a folder at a glance.
const FILE_TINTS: { exts: string[]; tint: FileTint }[] = [
  {
    exts: ['pdf'],
    tint: { tile: 'bg-rose-50', outline: 'stroke-rose-300', fold: 'fill-rose-300', line: 'stroke-rose-400' },
  },
  {
    exts: ['doc', 'docx', 'rtf', 'odt', 'pages'],
    tint: { tile: 'bg-blue-50', outline: 'stroke-blue-300', fold: 'fill-blue-300', line: 'stroke-blue-400' },
  },
  {
    exts: ['xls', 'xlsx', 'csv', 'tsv', 'numbers'],
    tint: { tile: 'bg-emerald-50', outline: 'stroke-emerald-300', fold: 'fill-emerald-300', line: 'stroke-emerald-400' },
  },
  {
    exts: ['ppt', 'pptx', 'key'],
    tint: { tile: 'bg-orange-50', outline: 'stroke-orange-300', fold: 'fill-orange-300', line: 'stroke-orange-400' },
  },
  {
    exts: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic', 'tiff'],
    tint: { tile: 'bg-violet-50', outline: 'stroke-violet-300', fold: 'fill-violet-300', line: 'stroke-violet-400' },
  },
]

const fileTint = (name?: string): FileTint => {
  const ext = name?.split('.').pop()?.toLowerCase() ?? ''
  return FILE_TINTS.find((group) => group.exts.includes(ext))?.tint ?? DEFAULT_TINT
}

export function FolderGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <path
        d="M2.5 7A2.5 2.5 0 0 1 5 4.5h3.9a2 2 0 0 1 1.45.62l1.1 1.16c.38.4.9.62 1.45.62H19A2.5 2.5 0 0 1 21.5 9.4V10h-19V7Z"
        className="fill-amber-500"
      />
      <path d="M2.5 9h19v7.5a2.5 2.5 0 0 1-2.5 2.5H5a2.5 2.5 0 0 1-2.5-2.5V9Z" className="fill-amber-400" />
      <path d="M2.5 9h19v1.6h-19z" className="fill-white/30" />
    </svg>
  )
}

export function NoteGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <path
        d="M4.5 4.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v10.4l-5.5 5.6H6.5a2 2 0 0 1-2-2v-14Z"
        className="fill-sky-400"
      />
      <path d="M14 20.5v-3.6a2 2 0 0 1 2-2h3.5L14 20.5Z" className="fill-sky-200" />
      <path
        d="M8 8h8M8 11.5h8M8 15h4"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="stroke-white/80"
      />
    </svg>
  )
}

export function FileGlyph({ name, className }: { name?: string; className?: string }) {
  const tint = fileTint(name)
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <path
        d="M5 4.5A2.5 2.5 0 0 1 7.5 2H14l5 5v12.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 5 19.5v-15Z"
        strokeWidth="1"
        className={cn('fill-white', tint.outline)}
      />
      <path d="M14 2l5 5h-3.5A1.5 1.5 0 0 1 14 5.5V2Z" className={tint.fold} />
      <path
        d="M8.5 11h7M8.5 14h7M8.5 17h4.5"
        strokeWidth="1.5"
        strokeLinecap="round"
        className={tint.line}
      />
    </svg>
  )
}

const TILE_SIZES = {
  sm: { tile: 'h-8 w-8 rounded-lg', glyph: 'h-[18px] w-[18px]' },
  lg: { tile: 'h-14 w-14 rounded-xl', glyph: 'h-8 w-8' },
}

interface VaultItemIconProps {
  kind: VaultKind
  name?: string
  size?: keyof typeof TILE_SIZES
  className?: string
  linked?: boolean
}

export function VaultItemIcon({ kind, name, size = 'sm', className, linked = false }: VaultItemIconProps) {
  const { tile, glyph } = TILE_SIZES[size]
  const tileColor =
    kind === 'folder' ? 'bg-amber-100' : kind === 'note' ? 'bg-sky-100' : fileTint(name).tile
  return (
    <span className={cn('relative flex shrink-0 items-center justify-center', tile, tileColor, className)}>
      {kind === 'folder' && <FolderGlyph className={glyph} />}
      {kind === 'note' && <NoteGlyph className={glyph} />}
      {kind === 'file' && <FileGlyph name={name} className={glyph} />}
      {linked && (
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-background bg-slate-800 text-white">
          <LockKeyhole className="h-2.5 w-2.5" />
        </span>
      )}
    </span>
  )
}
